import { useEffect } from "react";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy - Soundope";
  }, []);

  return (
    <div className="min-h-screen px-4 py-16">
      <div className="max-w-4xl mx-auto">
        <div className="neuro-base rounded-3xl p-8">
          <h1 className="text-3xl font-light text-[#d0d0d0] mb-6">Privacy Notice</h1>
          <p className="text-sm text-[#808080] mb-8">Last updated: November 03, 2025</p>

          <div className="space-y-6 text-sm text-[#909090] leading-relaxed">
            <p>
              This Privacy Notice for Soundope (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), describes how and why we might collect, store, 
              use, and/or share (&quot;process&quot;) your personal information when you use our Services (&quot;Services&quot;), including 
              when you visit our website at https://soundope.com, or any website of ours that links to this privacy notice.
            </p>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">Summary of Key Points</h2>
              <p className="mb-3">This summary provides key points from our Privacy Notice:</p>
              <ul className="space-y-2 ml-6">
                <li>• We process personal information based on how you interact with our Services</li>
                <li>• We do not process sensitive personal information</li>
                <li>• We may receive information from third parties like social media platforms</li>
                <li>• We process your information to provide and improve our Services</li>
                <li>• We may share information in specific situations with specific third parties</li>
                <li>• You have rights regarding your personal information based on your location</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">1. What Information Do We Collect?</h2>
              <p className="mb-3">
                <strong className="text-[#a0a0a0]">Personal Information You Disclose:</strong> We collect personal 
                information that you voluntarily provide when you register on the Services, express an interest in 
                obtaining information about us, participate in activities on the Services, or contact us.
              </p>
              <p className="mb-3">
                <strong className="text-[#a0a0a0]">Information Automatically Collected:</strong> Some information 
                (such as IP address, browser characteristics, device characteristics) is collected automatically when 
                you visit our Services. This information does not reveal your specific identity but may include device 
                and usage information.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">2. How Do We Process Your Information?</h2>
              <p>
                We process your personal information to provide, improve, and administer our Services, communicate with 
                you, for security and fraud prevention, and to comply with law. We may also process your information for 
                other purposes with your consent.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">3. When and With Whom Do We Share Information?</h2>
              <p className="mb-2">We may share information in specific situations:</p>
              <ul className="space-y-2 ml-6">
                <li>
                  <strong className="text-[#a0a0a0]">Business Transfers:</strong> In connection with mergers, sales, 
                  financing, or acquisition negotiations
                </li>
                <li>
                  <strong className="text-[#a0a0a0]">Affiliates:</strong> With our parent company, subsidiaries, or 
                  joint venture partners
                </li>
                <li>
                  <strong className="text-[#a0a0a0]">Business Partners:</strong> To offer you certain products, 
                  services, or promotions
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">4. Do We Use Cookies and Tracking Technologies?</h2>
              <p>
                We may use cookies and similar tracking technologies to collect and store your information. Some help 
                maintain security, detect crashes, fix bugs, save preferences, and assist with basic functions. We also 
                permit third parties to use tracking technologies for advertising and analytics. See our Cookie Notice 
                for details.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">5. How Do We Handle Your Social Logins?</h2>
              <p>
                If you register or log in using a social media account, we may have access to certain information about 
                you. We will receive profile information from your social media provider (name, email, friends list, 
                profile picture). We use this information only for purposes described in this Privacy Notice.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">6. Is Your Information Transferred Internationally?</h2>
              <p>
                Our servers are located in the United States. Your information may be transferred to, stored in, and 
                processed by us in our facilities and by third parties with whom we share information. We take all 
                necessary measures to protect your information in accordance with this Privacy Notice and applicable law.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">7. How Long Do We Keep Your Information?</h2>
              <p>
                We keep your personal information for as long as necessary to fulfill the purposes set out in this 
                Privacy Notice, unless a longer retention period is required or permitted by law (such as taxes, 
                accounting, or other legal requirements).
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">8. Do We Collect Information From Minors?</h2>
              <p>
                We do not knowingly solicit data from or market to children under 18 years of age. If we learn that 
                personal information from users less than 18 years old has been collected, we will deactivate the account 
                and promptly delete such data. Contact us at jknoedler@soundope.com if you become aware of any such data.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">9. What Are Your Privacy Rights?</h2>
              <p className="mb-3">
                You may review, change, or terminate your account at any time. Depending on your country, you may have 
                certain rights regarding your personal information:
              </p>
              <ul className="space-y-2 ml-6">
                <li>• <strong className="text-[#a0a0a0]">Withdrawing Consent:</strong> You have the right to withdraw 
                consent at any time</li>
                <li>• <strong className="text-[#a0a0a0]">Account Information:</strong> You may review or change 
                information in your account</li>
                <li>• <strong className="text-[#a0a0a0]">Account Termination:</strong> Request to deactivate or delete 
                your account</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">10. Controls for Do-Not-Track Features</h2>
              <p>
                Most browsers include a Do-Not-Track (DNT) feature. At this stage, no uniform technology standard for 
                recognizing and implementing DNT signals has been finalized. Therefore, we do not currently respond to 
                DNT browser signals. If a standard is adopted that we must follow, we will inform you in a revised 
                version of this Privacy Notice.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">11. Do We Make Updates to This Notice?</h2>
              <p>
                Yes, we will update this notice as necessary to stay compliant with relevant laws. The updated version 
                will be indicated by an updated &quot;Revised&quot; date at the top. We may notify you of material changes. We 
                encourage you to review this Privacy Notice frequently.
              </p>
            </div>

            <div className="neuro-flat rounded-2xl p-6 mt-8">
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">12. How Can You Contact Us?</h2>
              <p className="mb-3">For questions or comments about this notice, contact:</p>
              <div className="space-y-1 text-[#a0a0a0]">
                <p><strong>Dead Entertainment</strong></p>
                <p>6431 Old Hwy 99 S</p>
                <p>Yreka, CA 96097</p>
                <p>United States</p>
                <p className="mt-3">Email: jknoedler@soundope.com</p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-medium text-[#d0d0d0] mb-3">13. How Can You Review, Update, or Delete Data?</h2>
              <p>
                Depending on the laws of your country, you may have the right to request access to the personal 
                information we collect from you, details about how we have processed it, correct inaccuracies, or delete 
                your personal information. You may also have the right to request restriction of processing or object to 
                our processing. To exercise these rights, you may submit a data subject access request by contacting us.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}